import { ReactNode, createElement } from 'react';

import { css } from '@compiled/react';

import Lozenge, { ThemeAppearance } from '@atlaskit/lozenge';
import { media } from '@atlaskit/primitives/responsive';

import { Adg4Button, Adg4ButtonProps } from '@/components/adg4/button/src/button';
import { SanitizedRichText } from '@/components/confluence/util/sanitized-rich-text';
import { Product, Edition } from '@/components/sites-provider/helpers/products';
import { useProductIntent } from '@/components/sites-provider/helpers/use-product-intent';
import TryIntent from '@/components/try-intent/src/try-intent';
import { Link } from '@/libraries/design/components/link/src/link';
import { LinkProps } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';

import { Icon, IconNameTypes } from '../../../../libraries/wpl/foundations/src/icon';
import { Button, ButtonProps } from '../../button/src/button';
import { AspectRatios, Image, Picture, PictureProps } from '../../image/src/image';

export type HeaderTagTypes = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | undefined;
export type OrientationTypes = 'horizontal' | 'vertical';

export interface ButtonAndLinkProps {
	buttonProps: ButtonProps | Adg4ButtonProps;
	primaryLinkProps: LinkProps;
}

export interface ButtonAndSecondaryLinkProps {
	buttonProps: ButtonProps | Adg4ButtonProps;
	secondaryLinkProps: LinkProps;
}

export interface OneLinkProps {
	primaryLinkProps?: LinkProps;
}

export interface OneButtonProps {
	buttonProps: ButtonProps | Adg4ButtonProps;
}

export interface TwoLinksProps {
	primaryLinkProps: LinkProps;
	secondaryLinkProps: LinkProps;
}

export interface EyebrowProps {
	eyebrowText: string;
}

export type CTAProps =
	| OneButtonProps
	| OneLinkProps
	| ButtonAndLinkProps
	| ButtonAndSecondaryLinkProps
	| TwoLinksProps;

/**
 * @deprecated - Please use @/libraries/design/components/content-block instead
 */
export interface ContentBlockProps {
	headerText?: string | ReactNode;
	isBannerRichText?: boolean;
	lozenge?: string;
	lozengeAppearance?: ThemeAppearance;
	bodyText?: string | ReactNode;
	bodyComponent?: ReactNode;
	headerType?: HeaderTagTypes;
	footerHeader?: string;
	footerText?: string;
	/**
	 * @deprecated - Please use pictureProps instead
	 */
	imageLink?: string;
	/**
	 * @deprecated - Please use pictureProps instead
	 */
	imageAltText?: string;
	pictureProps?: PictureProps;
	decoration?: string;
	eyebrowText?: string;
	iconName?: IconNameTypes;
	iconColor?: string;
	iconSize?: 'xs' | 'small' | 'medium' | 'large';
	className?: string;
	ctaProps?: CTAProps;
	isCentered?: boolean;
	cardStyle?: boolean;
	/**
	 * @deprecated - Please use pictureProps instead
	 */
	aspectRatio?: AspectRatios;
	orientation?: OrientationTypes;
	horizontalRule?: boolean;
}

function getHeaderTag(
	text: string | ReactNode,
	header: HeaderTagTypes,
	lozenge: string | undefined,
	lozengeAppearance: ThemeAppearance,
) {
	const headerTag = header ? header : 'h3';
	const headerText = (
		<>
			{text}
			{lozenge && (
				<div
					css={css({
						marginInlineStart: '8px',
						display: 'inline-block',
					})}
				>
					<div
						css={css({
							display: 'inline-flex',
							verticalAlign: 'middle',
						})}
					>
						<Lozenge appearance={lozengeAppearance}>{lozenge}</Lozenge>
					</div>
				</div>
			)}
		</>
	);
	const style = lozenge ? { style: { display: 'inline-block' } } : null;
	return createElement(headerTag, style, headerText);
}

/* Gives p tag  so that page structure follows HTML semantic ordering */
function getEyebrowTag(
	// @ts-expect-error unused
	headerType: HeaderTagTypes | undefined,
	eyebrowText: string | undefined,
) {
	return createElement(`p`, { className: 'contentBlock-eyebrow' }, eyebrowText);
}

interface renderLinkProps {
	ctaProps?: CTAProps;
	product?: Product;
	edition?: Edition;
}

function renderLinks({ ctaProps, product, edition }: renderLinkProps) {
	if (!ctaProps) {
		return null;
	}
	let firstLink;
	if ('buttonProps' in ctaProps && 'text' in ctaProps.buttonProps) {
		firstLink = <Button {...ctaProps.buttonProps} />;
	} else if ('buttonProps' in ctaProps && 'variant' in ctaProps.buttonProps) {
		firstLink = <Adg4Button {...ctaProps.buttonProps} />;
	} else if ('primaryLinkProps' in ctaProps && ctaProps.primaryLinkProps) {
		// eslint-disable-next-line @atlassian/a11y/anchor-has-content
		firstLink = <Link {...ctaProps.primaryLinkProps} />;
	}
	const secondaryLink =
		'secondaryLinkProps' in ctaProps ? (
			// eslint-disable-next-line @atlassian/a11y/anchor-has-content
			<Link {...ctaProps.secondaryLinkProps} />
		) : null;
	return (
		<TryIntent
			intentType={'button'}
			containerId={'ctaSection'}
			edition={edition}
			productKey={product?.key}
		>
			<div className="contentBlock-linkFlex">
				{firstLink}
				{secondaryLink}
			</div>
		</TryIntent>
	);
}

/**
 * This method retrieves either the eyebrow or icon decoration. If both are present, the icon is returned.
 * @param headerType
 */
const getDecoration = (
	headerType?: HeaderTagTypes,
	eyebrowText?: string,
	iconName?: IconNameTypes,
	iconColor?: string,
	iconSize?: 'xs' | 'small' | 'medium' | 'large' | undefined,
) => {
	if (iconName) {
		return <Icon name={iconName} color={iconColor} size={iconSize} />;
	} else if (eyebrowText) {
		return getEyebrowTag(headerType, eyebrowText);
	}
	return null;
};

const getImage = (
	pictureProps?: PictureProps,
	imageLink?: string,
	imageAltText?: string,
	aspectRatio?: AspectRatios,
) => {
	if (pictureProps) {
		return (
			<div className="img-section">
				<Picture {...pictureProps} />
			</div>
		);
	} else if (imageLink && imageAltText) {
		return (
			<div className="img-section">
				<Image src={imageLink} alt={imageAltText} aspectRatio={aspectRatio} />
			</div>
		);
	} else {
		return null;
	}
};

const isHtml = (text: string) => {
	return text.startsWith('<') && text.endsWith('>') && text.includes('</');
};

/**
 * @deprecated - Please use @/libraries/design/components/content-block instead
 */
export const ContentBlock = ({
	headerText,
	isBannerRichText = false,
	lozenge,
	lozengeAppearance = 'inprogress',
	bodyText,
	bodyComponent,
	headerType = 'h3',
	eyebrowText,
	className = '',
	imageLink,
	imageAltText,
	pictureProps,
	iconName,
	iconColor,
	iconSize,
	ctaProps,
	aspectRatio = '1/1',
	orientation,
	cardStyle,
	isCentered = false,
	horizontalRule,
}: // eslint-disable-next-line complexity
ContentBlockProps) => {
	const { product, edition } = useProductIntent();

	if (imageLink && !imageAltText) {
		throw new Error('If using an image, please provide an alt text for accessibility purposes!');
	}
	return (
		<div
			className={`content-block ${className}`}
			css={[
				isBannerRichText &&
					css({
						display: 'flex',
						flexDirection: 'column',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& + .text-section': {
								marginBlockStart: '0.5rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'flex-start',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					isCentered &&
					orientation === 'horizontal' &&
					cardStyle &&
					headerType === 'h1' &&
					css({
						// added to avoid breaking change since >1000 lines of css to refactor and is WPL component used in 70+ other files
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
						gap: '1rem',
						[media.below.xs]: {
							gridTemplateColumns: 'minmax(0, 1fr)',
							gap: '0',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
								height: '100%',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							padding: '20px',
							textAlign: 'center',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
							fontSize: '1.25rem',
							letterSpacing: '0.018rem',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'center',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					isCentered &&
					orientation === 'horizontal' &&
					!cardStyle &&
					headerType === 'h1' &&
					css({
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
						gap: '1rem',
						[media.below.xs]: {
							gridTemplateColumns: 'minmax(0, 1fr)',
							gap: '0',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
								height: '100%',
							},
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& + .text-section': {
								marginBlockStart: '0.5rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							textAlign: 'center',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
							fontSize: '1.25rem',
							letterSpacing: '0.018rem',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'center',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					!isCentered &&
					orientation === 'horizontal' &&
					cardStyle &&
					headerType === 'h1' &&
					css({
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
						gap: '1rem',
						[media.below.xs]: {
							gridTemplateColumns: 'minmax(0, 1fr)',
							gap: '0',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							padding: '20px',
							textAlign: 'left',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
							fontSize: '1.25rem',
							letterSpacing: '0.018rem',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'flex-start',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					!isCentered &&
					orientation === 'horizontal' &&
					!cardStyle &&
					headerType === 'h1' &&
					css({
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
						gap: '1rem',
						[media.below.xs]: {
							gridTemplateColumns: 'minmax(0, 1fr)',
							gap: '0',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& + .text-section': {
								marginBlockStart: '0.5rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							textAlign: 'left',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',

							fontSize: '1.25rem',
							letterSpacing: '0.018rem',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'flex-start',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					isCentered &&
					orientation === 'horizontal' &&
					cardStyle &&
					headerType !== 'h1' &&
					css({
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
						gap: '1rem',
						[media.below.xs]: {
							gridTemplateColumns: 'minmax(0, 1fr)',
							gap: '0',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							padding: '20px',
							textAlign: 'center',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'center',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					isCentered &&
					orientation === 'horizontal' &&
					!cardStyle &&
					headerType !== 'h1' &&
					css({
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
						gap: '1rem',
						[media.below.xs]: {
							gridTemplateColumns: 'minmax(0, 1fr)',
							gap: '0',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
								height: '100%',
							},
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& + .text-section': {
								marginBlockStart: '0.5rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							textAlign: 'center',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'center',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					!isCentered &&
					orientation === 'horizontal' &&
					cardStyle &&
					headerType !== 'h1' &&
					css({
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
						gap: '1rem',
						[media.below.xs]: {
							gridTemplateColumns: 'minmax(0, 1fr)',
							gap: '0',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							padding: '20px',
							textAlign: 'left',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'flex-start',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					!isCentered &&
					orientation === 'horizontal' &&
					!cardStyle &&
					headerType !== 'h1' &&
					css({
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
						gap: '1rem',
						[media.below.xs]: {
							gridTemplateColumns: 'minmax(0, 1fr)',
							gap: '0',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& + .text-section': {
								marginBlockStart: '0.5rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							textAlign: 'left',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'flex-start',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					isCentered &&
					orientation !== 'horizontal' &&
					cardStyle &&
					headerType === 'h1' &&
					css({
						display: 'flex',
						flexDirection: 'column',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							padding: '20px',
							textAlign: 'center',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
							fontSize: '1.25rem',
							letterSpacing: '0.018rem',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'center',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					isCentered &&
					orientation !== 'horizontal' &&
					!cardStyle &&
					headerType !== 'h1' &&
					css({
						display: 'flex',
						flexDirection: 'column',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							textAlign: 'center',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'center',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					!isCentered &&
					orientation !== 'horizontal' &&
					cardStyle &&
					headerType === 'h1' &&
					css({
						display: 'flex',
						flexDirection: 'column',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							padding: '20px',
							textAlign: 'left',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',

							fontSize: '1.25rem',
							letterSpacing: '0.018rem',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'flex-start',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					!isCentered &&
					orientation !== 'horizontal' &&
					!cardStyle &&
					headerType === 'h1' &&
					css({
						display: 'flex',
						flexDirection: 'column',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& + .text-section': {
								marginBlockStart: '0.5rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							textAlign: 'left',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',

							fontSize: '1.25rem',
							letterSpacing: '0.018rem',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'flex-start',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					isCentered &&
					orientation !== 'horizontal' &&
					cardStyle &&
					headerType !== 'h1' &&
					css({
						display: 'flex',
						flexDirection: 'column',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							padding: '20px',
							textAlign: 'center',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'center',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					!isCentered &&
					orientation !== 'horizontal' &&
					cardStyle &&
					headerType !== 'h1' &&
					css({
						display: 'flex',
						flexDirection: 'column',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							padding: '20px',
							textAlign: 'left',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'flex-start',
							alignItems: 'center',
						},
					}),
				!isBannerRichText &&
					!isCentered &&
					orientation !== 'horizontal' &&
					!cardStyle &&
					headerType !== 'h1' &&
					css({
						display: 'flex',
						flexDirection: 'column',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.img-section': {
							lineHeight: 0,
							display: 'block',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							img: {
								width: '100%',
							},
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& + .text-section': {
								marginBlockStart: '0.5rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.text-section': {
							textAlign: 'left',
							display: 'flex',
							flexDirection: 'column',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
							'& > *': {
								margin: '0 0 24px',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'h1, h2, h3, h4, h5, h6': {
							fontWeight: '500',
							lineHeight: '1.16',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h1: {
							fontSize: '3rem',
							[media.only.sm]: {
								fontSize: '2.75rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h2: {
							fontSize: '2.75rem',
							[media.only.sm]: {
								fontSize: '2.5rem',
							},
							[media.below.sm]: {
								fontSize: '2.25rem',
							},
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h3: {
							fontSize: '1.5rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						h4: {
							fontSize: '1.25rem',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						p: {
							lineHeight: '1.4',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'a, button': {
							marginBlockStart: '0.5rem',
							width: 'fit-content',
							fontWeight: '500',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-body': {
							fontWeight: '400',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-eyebrow': {
							fontSize: '0.75rem',
							fontWeight: '600',
							letterSpacing: '0.1rem',
							textTransform: 'uppercase',
							order: '-1',
							fontFamily: wacToken('font.charlie.display'),
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.icon': {
							order: '-1',
						},
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
						'.contentBlock-linkFlex': {
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10%',
							justifyContent: 'flex-start',
							alignItems: 'center',
						},
					}),
			]}
		>
			{/* Header is first in HTML for accessibility but order is changed visually through CSS */}
			{getImage(pictureProps, imageLink, imageAltText, aspectRatio)}

			<div className="text-section">
				{headerText && getHeaderTag(headerText, headerType, lozenge, lozengeAppearance)}

				{getDecoration(headerType, eyebrowText, iconName, iconColor, iconSize)}

				{horizontalRule && (
					<hr
						role="presentation"
						css={css({
							height: '2px',
							maxWidth: '1200px',
						})}
					/>
				)}

				{bodyText ? (
					typeof bodyText === 'string' && isHtml(bodyText) ? (
						<SanitizedRichText innerHTML={bodyText} />
					) : (
						<p className="contentBlock-body">{bodyText}</p>
					)
				) : null}
				{/* Adding bodyComponent, since adding <div> as a descendant of <p> throws validateDOMNesting error. */}
				{!bodyText && bodyComponent && <span className="contentBlock-body">{bodyComponent}</span>}

				{renderLinks({ ctaProps, product, edition })}
			</div>
		</div>
	);
};
